import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import OrderTruckService from '../../../../../services/orders/trucks';

// props
import { IDocumentProps, IFileDetailsProps } from '../../../../../props/general';

// components
import {
    CommandBar,
    DefaultButton,
    ICommandBarItemProps,
    PrimaryButton,
    Stack,
} from '@fluentui/react';
import FilesList from '../../../../uiframeworks/files/list';
import UploadFilesPanel from '../../../../uiframeworks/files/uploads/panel';
import DeleteFilesPanel from '../../../../uiframeworks/files/delete';
import PermissionsService from '../../../../../services/permissions';
import { OrderDocumentTypes } from '../../../../../manifests/documentTypes';

interface IOrderTruckDocumentsListProps {
    orderId: string;
    truckId: string;
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideDeleteButton?: boolean;
    hideCreateButton?: boolean;
}

const OrderTruckDocumentsList: React.FC<IOrderTruckDocumentsListProps> = (
    props: IOrderTruckDocumentsListProps
) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [documents, setDocuments] = React.useState<IDocumentProps[]>([]);
    const [selected, setSelected] = React.useState<IDocumentProps[]>([]);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    useEffect(() => {
        _onRetrieveDocuments();
    }, [props.orderId, props.truckId]);

    const _onRetrieveDocuments = async () => {
        try {
            setLoaded(false);
            setSelected([]);
            const result = await OrderTruckService.documents.retrieve(
                props.orderId,
                props.truckId
            );
            setDocuments(result);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_truck_documents_error',
                text: 'Failed to retrieve truck documents. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error',
            });
        }
    };

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        const hasDeleteAccess = PermissionsService.hasPermission(
            ['orders.update.all'],
            user.permissions
        );

        if (!props.hideDeleteButton && selected.length > 0 && hasDeleteAccess) {
            items.push({
                key: 'delete',
                onRender: () => (
                    <DefaultButton
                        text={'Delete Document(s)'}
                        iconProps={{ iconName: 'Delete' }}
                        onClick={() => setActiveSurface('delete')}
                        styles={{ root: { marginLeft: 10 } }}
                    />
                ),
            });
        }

        if (
            !props.hideCreateButton &&
            PermissionsService.hasPermission(['orders.update.all'], user.permissions)
        ) {
            farItems.push({
                key: 'upload',
                onRender: () => (
                    <PrimaryButton
                        text={'Upload Document(s)'}
                        iconProps={{ iconName: 'Upload' }}
                        onClick={() => setActiveSurface('upload')}
                        styles={{ root: { marginLeft: 10 } }}
                    />
                ),
            });
        }

        return { items, farItems };
    };

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onRetrieveDocuments(); }
    };

    const _onUploadDocuments = async (files: IFileDetailsProps[]) => {
        try {
            const fd = new FormData();
            files.forEach((f) => {
                const file = f.data as File;
                fd.append(
                    'metadatas[]',
                    JSON.stringify({ visibility: f.visibility, type: f.type, name: file.name, size: file.size })
                );
            });
            files.forEach((f) => fd.append('documents[]', f.data));

            await OrderTruckService.documents.upload(props.orderId, props.truckId, fd);

            banner.add({
                key: 'upload_truck_documents_success',
                variant: 'success',
                icon: faCheck,
                text: `Document(s) uploaded successfully.`,
            });

            setActiveSurface(undefined);
            _onRetrieveDocuments();
        } catch (e) {
            throw e;
        }
    };

    const _onDeleteDocuments = async (files: IDocumentProps[]) => {
        try {
            const fd = new FormData();
            const names: string[] = [];
            files.forEach((file) => {
                fd.append('deleteDocumentIds[]', file.id);
                names.push(`"${file.basename}"`);
            });

            await OrderTruckService.documents.delete(props.orderId, props.truckId, fd);

            banner.add({
                key: 'delete_truck_documents_success',
                variant: 'success',
                icon: faCheck,
                text: `Document(s) ${names.join(', ')} deleted successfully.`,
            });

            setActiveSurface(undefined);
            _onRetrieveDocuments();
        } catch (e) {
            throw e;
        }
    };

    const _onSelect = (_selected: IDocumentProps[]) => {
        setSelected([..._selected]);
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {!props.hideCommandBar ? (
                <CommandBar
                    items={getCommandBarItems().items}
                    farItems={getCommandBarItems().farItems}
                    styles={{
                        root: {
                            padding: 0,
                            height: 'unset',
                            backgroundColor: 'transparent',
                        },
                    }}
                    ariaLabel="Truck documents actions"
                />
            ) : null}
            <FilesList
                documentTypes={OrderDocumentTypes}
                files={documents}
                loaded={loaded}
                onSelectFile={_onSelect}
                selected={selected}
            />
            {activeSurface === 'upload' ? (
                <UploadFilesPanel
                    multiple={true}
                    existings={documents}
                    title={'Upload Truck Document(s)'}
                    onCancel={_onSurfaceDismissed}
                    onUpload={_onUploadDocuments}
                />
            ) : null}
            {activeSurface === 'delete' && selected.length > 0 ? (
                <DeleteFilesPanel
                    title={'Delete Document(s)'}
                    files={selected}
                    onCancel={_onSurfaceDismissed}
                    onDelete={_onDeleteDocuments}
                />
            ) : null}
        </Stack>
    );
};

export default OrderTruckDocumentsList;

